import { useAuthStore } from '@/store/authStore';
import { API_BASE, CP_BASE } from '@/lib/http';
import type { AppInstance, AppKey } from './types';

type Json = Record<string, any>;

function getAuthHeaders(): Headers {
  const headers = new Headers();
  headers.set('Content-Type', 'application/json');

  try {
    const s = useAuthStore.getState();
    if (s?.token) headers.set('Authorization', `Bearer ${s.token}`);
    if (s?.selectedTenant) headers.set('x-tenant-id', s.selectedTenant);
  } catch {
    // ignore
  }

  // Back-compat: allow legacy localStorage auth
  try {
    const raw = localStorage.getItem('dc.auth');
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${parsed.token}`);
    if (parsed?.tenant && !headers.has('x-tenant-id')) headers.set('x-tenant-id', String(parsed.tenant));
  } catch {
    // ignore
  }

  if (!headers.has('x-tenant-id')) headers.set('x-tenant-id', 'dev');
  // App context is stored separately (used by many CP routes).
  try {
    const appId = localStorage.getItem('dc.app_id');
    if (appId && !headers.has('x-app-id')) headers.set('x-app-id', String(appId));
  } catch {
    // ignore
  }
  return headers;
}

function getAuthHeadersForApp(app_id: string): Headers {
  const headers = getAuthHeaders();
  headers.set('x-app-id', String(app_id));
  return headers;
}

async function apiRequest<T>(path: string, init: RequestInit): Promise<T> {
  // If path is a full URL, use as-is.
  // If path already contains API or CP prefix (e.g., /api/... or /cp/...),
  // don't prepend API_BASE again.
  // Otherwise prepend API_BASE.
  let url: string;
  if (path.startsWith('http')) {
    url = path;
  } else if (API_BASE && path.startsWith(API_BASE)) {
    url = path;
  } else if (CP_BASE && path.startsWith(CP_BASE)) {
    url = path; // Already has /cp prefix, use as relative URL
  } else {
    url = `${API_BASE}${path}`;
  }
  const res = await fetch(url, { credentials: 'omit', cache: 'no-store', ...init });
  const text = await res.text().catch(() => '');
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) ? `${data.error || data.message}` : `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
  return data as T;
}

// Apps API base path. Uses API_BASE (/api) - Kong JWT removed from this route,
// orchestrator's own auth guard handles Firebase JWT validation.
const APPS_BASE = `${API_BASE}/apps`;

export async function listAppInstances(): Promise<{ apps: AppInstance[] }> {
  return apiRequest(`${APPS_BASE}`, { method: 'GET', headers: getAuthHeaders() });
}

export async function createAppInstance(input: {
  name: string;
  description?: string;
  features?: Record<string, boolean>;
  async?: '1' | '0';
  data?: {
    storage_mode?: 'shared' | 'dedicated';
    mongo_uri?: string;
    source_types?: string[];
  };
}): Promise<{ app_id: string; name: string; description?: string | null; status: string; api_key: { key_id: string; key: string }; mcp?: any; hosting?: any }> {
  return apiRequest(`${APPS_BASE}`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(input) });
}

export async function testMongoConnection(uri: string): Promise<{ ok: boolean; code?: string; detail?: string; database?: string }> {
  const path = `${APPS_BASE}/test-mongo-connection`;
  let url: string;
  if (path.startsWith('http')) {
    url = path;
  } else if (API_BASE && path.startsWith(API_BASE)) {
    url = path;
  } else {
    url = `${API_BASE}${path}`;
  }

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    headers: getAuthHeaders(),
    body: JSON.stringify({ uri }),
  });
  const text = await res.text().catch(() => '');
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  // Validation failures return 400 with { ok:false, code, detail } — not a transport error.
  if (res.status === 400 && data && typeof data === 'object' && data.ok === false) {
    return data;
  }
  if (!res.ok) {
    const msg = (data && (data.error || data.message || data.detail || data.code))
      ? String(data.error || data.message || data.detail || data.code)
      : `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
  return data as { ok: boolean; code?: string; detail?: string; database?: string };
}

export async function getAppInstance(app_id: string): Promise<AppInstance & { keys?: AppKey[] }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}`, { method: 'GET', headers: getAuthHeaders() });
}

export async function retryProvisioning(app_id: string): Promise<{ success: boolean; status: string; error?: string | null }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/retry-provisioning`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({}) });
}

export async function createAppKey(app_id: string, input: { name?: string; scopes?: string[] }): Promise<{ key_id: string; key: string }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/keys`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(input || {}) });
}

export async function revokeAppKey(app_id: string, key_id: string): Promise<{ success: true } | any> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/keys/${encodeURIComponent(key_id)}`, { method: 'DELETE', headers: getAuthHeaders() });
}

export async function updateAppInstance(app_id: string, patch: Json): Promise<AppInstance> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(patch || {}) });
}

export async function deleteAppInstance(app_id: string): Promise<{ success: true }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}`, { method: 'DELETE', headers: getAuthHeaders() });
}

export type AppSecretSummary = { key: string; present: boolean; last4: string | null };

export async function listAppSecretSummaries(app_id: string): Promise<{ secrets: AppSecretSummary[] }> {
  return apiRequest<{ secrets: AppSecretSummary[] }>(`${APPS_BASE}/${encodeURIComponent(app_id)}/secret-summaries`, {
    method: 'GET',
    headers: getAuthHeadersForApp(app_id),
  });
}

export async function getAppSettings(app_id: string): Promise<any> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/settings`, {
    method: 'GET',
    headers: getAuthHeadersForApp(app_id),
  });
}

export async function updateAppSettings(app_id: string, settingsPatch: Json): Promise<any> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/settings`, {
    method: 'PATCH',
    headers: getAuthHeadersForApp(app_id),
    body: JSON.stringify({ settings: settingsPatch }),
  });
}

export async function setAppDedicatedMongoUri(app_id: string, input: { env?: 'dev' | 'staging' | 'prod'; value: string; reason?: string; tenant?: string }): Promise<{ ok: boolean; secret_ref?: string }> {
  const env = input?.env || 'dev';
  return apiRequest(`${CP_BASE}/secrets/apps/${encodeURIComponent(app_id)}/mongodb-uri`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      env,
      tenant: input?.tenant,
      value: input?.value || '',
      reason: input?.reason || `backend_builder:${app_id}:dedicated_mongodb`,
    }),
  });
}

export async function getAppMcpHandoff(app_id: string): Promise<{ success: boolean; markdown: string; filename: string }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/mcp-handoff`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
}

export async function getAppCursorRules(app_id: string): Promise<{
  success: boolean;
  context?: string;
  workflow_schemas?: Record<string, any>;
  rules_markdown?: string;
  mcp_json?: string;
  setup_prompt?: string;
  manifest?: Record<string, any>;
}> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/cursor-rules`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
}

export async function provisionAppInstance(app_id: string): Promise<{ success: boolean; provision?: any; status?: string }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/provision`, { 
    method: 'POST', 
    headers: getAuthHeaders(),
    body: JSON.stringify({ async: "1" }) // Force async mode for the UI
  });
}

export async function runProvisionDoctor(app_id: string): Promise<{ success: boolean; status?: string; provision?: any; error?: string | null }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/provision/doctor`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
}

export async function verifyAppInstance(app_id: string): Promise<{ success: boolean; verification?: any }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/verify`, { method: 'POST', headers: getAuthHeaders() });
}

export async function listAgentExecutions(input?: { limit?: number; tenant_id?: string }): Promise<{ executions: any[]; count?: number }> {
  const qs = new URLSearchParams();
  if (input?.limit != null) qs.set('limit', String(input.limit));
  if (input?.tenant_id) qs.set('tenant_id', String(input.tenant_id));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest(`${CP_BASE}/api/agents/executions${suffix}`, { method: 'GET', headers: getAuthHeaders() });
}

export async function createAdminUser(input: { email: string; password: string }): Promise<{ success: boolean; user_id: string; email: string; roles: string[]; tenant_id: string }> {
  return apiRequest(`${CP_BASE}/auth/firebase/users`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });
}

export async function listAppAuditEvents(app_id: string, input?: { limit?: number; type?: string; before?: string }): Promise<{ events: any[] }> {
  const limit = input?.limit != null ? Number(input.limit) : 50;
  const type = input?.type != null ? String(input.type) : '';
  const before = input?.before != null ? String(input.before) : '';
  const qs = new URLSearchParams();
  if (Number.isFinite(limit)) qs.set('limit', String(limit));
  if (type) qs.set('type', type);
  if (before) qs.set('before', before);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/audit-events${suffix}`, { method: 'GET', headers: getAuthHeaders() });
}

export async function listAppHandoffPosts(app_id: string, input?: { limit?: number }): Promise<{ posts: any[] }> {
  const limit = input?.limit != null ? Number(input.limit) : 20;
  const qs = new URLSearchParams();
  if (Number.isFinite(limit)) qs.set('limit', String(limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/handoff-posts${suffix}`, { method: 'GET', headers: getAuthHeaders() });
}

export async function listAppMembers(app_id: string): Promise<{ members: any[] }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/members`, { method: 'GET', headers: getAuthHeaders() });
}

export async function upsertAppMember(app_id: string, input: { user_id?: string; email?: string; role: string }): Promise<any> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/members`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input || {}),
  });
}

export async function removeAppMember(app_id: string, user_id: string): Promise<any> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/members/${encodeURIComponent(user_id)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}

const LAYOUT_AI_PROMPT = `You are a Visual Layout assistant for DevCockpit app instances.
Given the user message and current layout context, return STRICT JSON only:
{
  "layout": {
    "components": [{ "id": "comp_1", "name": "Service", "summary": "why it exists", "connections": ["comp_2"] }],
    "dataStores": [{ "id": "db_1", "name": "Products DB", "summary": "what it stores" }],
    "apis": [{ "id": "api_1", "name": "Public API", "summary": "who uses it" }],
    "groups": [{ "id": "group_1", "name": "Core", "summary": "group purpose", "members": ["comp_1"] }],
    "notes": ["plain language note"]
  }
}

USER_INPUT:
{{input}}`;

function pickLlmText(resp: any): string {
  if (!resp) return '';
  if (typeof resp === 'string') return resp;

  // Raw volcano executor: { steps: [{ result: "..." }] }
  const steps = Array.isArray(resp?.steps) ? resp.steps : [];
  for (let i = steps.length - 1; i >= 0; i--) {
    const stepResult = steps[i]?.result;
    if (stepResult == null || stepResult === '') continue;
    if (typeof stepResult === 'string') return stepResult;
    if (typeof stepResult === 'object') {
      if (stepResult.response) return String(stepResult.response);
      if (stepResult.output) return String(stepResult.output);
      if (stepResult.text) return String(stepResult.text);
      return JSON.stringify(stepResult, null, 2);
    }
  }

  if (resp?.result?.internal_data?.response) return String(resp.result.internal_data.response);
  if (resp?.internal_data?.response) return String(resp.internal_data.response);
  if (resp?.data?.response) return String(resp.data.response);
  if (resp?.result?.response) return String(resp.result.response);
  if (resp?.result?.output) return String(resp.result.output);
  if (resp?.result?.text) return String(resp.result.text);
  if (resp?.user_message && typeof resp.user_message === 'string') return resp.user_message;
  return JSON.stringify(resp, null, 2);
}

function layoutHasNodes(layout: Record<string, unknown>): boolean {
  const keys = ['components', 'dataStores', 'apis', 'groups'] as const;
  return keys.some((key) => {
    const list = (layout as any)[key];
    return Array.isArray(list) && list.length > 0;
  });
}

function extractLayoutFromLlmResponse(text: string): Record<string, unknown> | null {
  const trimmed = String(text || '').trim();
  if (!trimmed) return null;
  const tryParse = (raw: string) => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  let parsed = tryParse(trimmed);
  if (!parsed) {
    const match = trimmed.match(/\{[\s\S]*\}/);
    if (match) parsed = tryParse(match[0]);
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const layout = (parsed as any).layout;
  if (layout && typeof layout === 'object' && !Array.isArray(layout)) {
    return layout as Record<string, unknown>;
  }
  if (!('layout' in (parsed as object))) {
    return parsed as Record<string, unknown>;
  }
  return null;
}

export async function patchAppLayout(app_id: string, layout: Record<string, unknown>): Promise<any> {
  return updateAppSettings(app_id, { builder: { layout } });
}

export async function requestLayoutFromAI(input: {
  app_id: string;
  app_name?: string | null;
  message: string;
  current_layout?: Record<string, unknown>;
}): Promise<Record<string, unknown>> {
  const payload = JSON.stringify({
    message: input.message,
    app: { id: input.app_id, name: input.app_name || null },
    layout: input.current_layout || {},
  });
  // /volcano/execute wraps LLM output in a conversation envelope (workflow_state: processing, data: null).
  // /volcano/execute/raw returns the synchronous executor payload with steps[].result text.
  const resp = await apiRequest('/volcano/execute/raw', {
    method: 'POST',
    headers: getAuthHeadersForApp(input.app_id),
    body: JSON.stringify({
      agent_definition: {
        id: 'app-layout-assistant',
        type: 'volcano',
        volcano_workflow: {
          steps: [
            {
              name: 'layout',
              provider: 'anthropic',
              model: 'claude-haiku-4-5-20251001',
              prompt: LAYOUT_AI_PROMPT,
              input: '{{input}}',
              timeout: 60000,
            },
          ],
        },
      },
      input: payload,
    }),
  });
  if (resp && typeof resp === 'object' && resp.success === false) {
    const err = (resp as any).error || 'volcano_execute_failed';
    throw new Error(String(err));
  }
  const layout = extractLayoutFromLlmResponse(pickLlmText(resp));
  if (!layout || !layoutHasNodes(layout)) {
    throw new Error('layout_not_in_response');
  }
  return layout;
}

// ── App Agents ──────────────────────────────────────────────────────────────

export async function listAppAgents(appId: string, tenant_id?: string | null): Promise<{ agents: any[]; allowlist?: any[] }> {
  const headers = tenant_id !== undefined
    ? getCompositionHeaders(appId, tenant_id)
    : getAuthHeadersForApp(appId);
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(appId)}/agents`, { method: 'GET', headers });
}

/** Headers for VLB composition reads. Never stamp leftover tenant_id:dev. */
function getCompositionHeaders(app_id: string, tenant_id?: string | null): Headers {
  const headers = getAuthHeaders();
  headers.set('x-app-id', String(app_id));
  const tenant = String(tenant_id || '').trim();
  if (tenant) {
    headers.set('x-tenant-id', tenant);
  } else {
    headers.delete('x-tenant-id');
  }
  return headers;
}

export type HostedSurfaceResponse = {
  app_id: string;
  readable: boolean;
  empty_reason?: string | null;
  files?: string[];
  sandbox?: { deployed?: boolean; files?: string[] };
  production?: { deployed?: boolean; files?: string[] };
};

export async function getAppHostedSurface(
  app_id: string,
  tenant_id?: string | null,
): Promise<HostedSurfaceResponse> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/hosted-surface`, {
    method: 'GET',
    headers: getCompositionHeaders(app_id, tenant_id),
  });
}

export async function listAppWorkspaceIngest(
  app_id: string,
  tenant_id?: string | null,
): Promise<{ items?: any[]; pipelines?: any[] }> {
  const qs = new URLSearchParams({ app_id, limit: '200' });
  return apiRequest(`${CP_BASE}/api-ingest/workspace/apis?${qs.toString()}`, {
    method: 'GET',
    headers: getCompositionHeaders(app_id, tenant_id),
  });
}

export async function listAppIotInventory(
  app_id: string,
  tenant_id?: string | null,
): Promise<{ devices: any[]; gateways: any[] }> {
  const headers = getCompositionHeaders(app_id, tenant_id);
  const qs = new URLSearchParams({ app_id, limit: '100' });
  const [devicesResp, gatewaysResp] = await Promise.all([
    apiRequest<any>(`${CP_BASE}/iot/devices?${qs.toString()}`, { method: 'GET', headers }),
    apiRequest<any>(`${CP_BASE}/iot/gateways?${qs.toString()}`, { method: 'GET', headers }).catch(() => ({ items: [] })),
  ]);
  const devices = Array.isArray(devicesResp)
    ? devicesResp
    : Array.isArray(devicesResp?.items)
      ? devicesResp.items
      : [];
  const gateways = Array.isArray(gatewaysResp)
    ? gatewaysResp
    : Array.isArray(gatewaysResp?.items)
      ? gatewaysResp.items
      : [];
  return { devices, gateways };
}

export async function listAppExposeBindings(
  app_id: string,
  tenant_id?: string | null,
): Promise<{ bindings: any[] }> {
  const data = await apiRequest<any>(`${CP_BASE}/api-expose/data-sources/${encodeURIComponent(app_id)}`, {
    method: 'GET',
    headers: getCompositionHeaders(app_id, tenant_id),
  });
  return { bindings: Array.isArray(data?.bindings) ? data.bindings : [] };
}

/** Tenant-wide MCP inventory without leftover x-app-id (avoids app_scope_violation). */
export async function listExposeMcpEndpoints(tenant_id?: string | null): Promise<{ endpoints: any[] }> {
  const headers = getCompositionHeaders('', tenant_id);
  headers.delete('x-app-id');
  return apiRequest(`${CP_BASE}/api-expose/mcp/endpoints`, { method: 'GET', headers });
}

export async function upsertAppExposeBinding(
  app_id: string,
  tenant_id: string | null | undefined,
  body: {
    source_type: string;
    source_id?: string | null;
    policy_ref?: { kind?: string | null; id?: string | null };
    scopes?: string[];
    filters?: Record<string, unknown>;
    rate_limit?: { rpm?: number | null } | null;
    enabled?: boolean;
  },
): Promise<any> {
  return apiRequest(`${CP_BASE}/api-expose/data-sources/${encodeURIComponent(app_id)}/by-source`, {
    method: 'PUT',
    headers: getCompositionHeaders(app_id, tenant_id),
    body: JSON.stringify(body),
  });
}

/** Connector probes (e.g. AquaCheck) this app can claim: unclaimed + already its own. */
export async function listAppConnectorProfiles(
  app_id: string,
  tenant_id: string | null | undefined,
  provider_key = 'aquacheck',
): Promise<{ api_id?: string; profiles: Array<{ data_id: string; name?: string | null; device_id: string; claimed?: boolean }> }> {
  const data = await apiRequest<any>(
    `${CP_BASE}/api-expose/connectors/${encodeURIComponent(app_id)}/profiles?provider_key=${encodeURIComponent(provider_key)}`,
    { method: 'GET', headers: getCompositionHeaders(app_id, tenant_id) },
  );
  return { api_id: data?.api_id, profiles: Array.isArray(data?.profiles) ? data.profiles : [] };
}

export async function claimAppConnectorProbes(
  app_id: string,
  tenant_id: string | null | undefined,
  body: { data_ids: string[]; provider_key?: string; every_seconds?: number },
): Promise<any> {
  return apiRequest(`${CP_BASE}/api-expose/connectors/${encodeURIComponent(app_id)}/claim`, {
    method: 'POST',
    headers: getCompositionHeaders(app_id, tenant_id),
    body: JSON.stringify({ provider_key: 'aquacheck', ...body }),
  });
}

export async function releaseAppConnectorProbes(
  app_id: string,
  tenant_id: string | null | undefined,
  body: { data_ids: string[]; provider_key?: string },
): Promise<any> {
  return apiRequest(`${CP_BASE}/api-expose/connectors/${encodeURIComponent(app_id)}/release`, {
    method: 'POST',
    headers: getCompositionHeaders(app_id, tenant_id),
    body: JSON.stringify({ provider_key: 'aquacheck', ...body }),
  });
}

export async function deleteAppExposeBinding(
  app_id: string,
  tenant_id: string | null | undefined,
  binding_id: string,
): Promise<any> {
  return apiRequest(
    `${CP_BASE}/api-expose/data-sources/${encodeURIComponent(app_id)}/${encodeURIComponent(binding_id)}`,
    {
      method: 'DELETE',
      headers: getCompositionHeaders(app_id, tenant_id),
    },
  );
}

export async function createAppAgent(appId: string, data: { name: string; description: string }): Promise<any> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(appId)}/agents`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
}

// ── MCP Management ──────────────────────────────────────────────────────────

export interface McpConfig {
  endpoint: string;
  key?: string;
  key_id?: string;
  cursor_config: {
    mcpServers: Record<string, { url: string; headers: { Authorization: string } }>;
  };
}

export async function provisionMcpKey(appId: string): Promise<McpConfig & { success: boolean; already_provisioned?: boolean }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(appId)}/mcp/provision`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
}

export async function toggleAppMcp(appId: string, enabled: boolean, reason?: string): Promise<{ success: boolean; mcp_enabled: boolean }> {
  return apiRequest(`${CP_BASE}/api-expose/mcp/endpoints/${encodeURIComponent(appId)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ enabled, disabled_reason: reason }),
  });
}

export type McpKeyFingerprint = {
  key_id: string;
  status?: string;
  lookup_hmac_prefix12?: string | null;
  superseded_by?: string | null;
};

export async function rotateAppMcpKey(appId: string): Promise<{
  success: boolean;
  new_key?: string;
  key?: string;
  key_id?: string;
  fingerprint?: McpKeyFingerprint;
  previous_keys_still_active?: McpKeyFingerprint[];
  note?: string;
  revocation?: { is_separate_act?: boolean; method?: string; path?: string; note?: string };
}> {
  return apiRequest(`${CP_BASE}/api-expose/mcp/endpoints/${encodeURIComponent(appId)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ rotate_key: true }),
  });
}

export async function getMcpEndpoints(): Promise<{ endpoints: any[] }> {
  return apiRequest(`${CP_BASE}/api-expose/mcp/endpoints`, { method: 'GET', headers: getAuthHeaders() });
}

export async function getMcpStats(appId: string): Promise<any> {
  return apiRequest(`${CP_BASE}/api-expose/mcp/endpoints/${encodeURIComponent(appId)}/stats`, { method: 'GET', headers: getAuthHeaders() });
}

// ── Hosting ─────────────────────────────────────────────────────────────────

export async function setCustomDomain(
  appId: string,
  domain: string,
  extra: { environment?: 'production' | 'sandbox'; confirm_tls?: boolean } = {},
): Promise<any> {
  return updateAppSettings(appId, {
    hosting: {
      custom_domain: domain,
      ...(extra.environment ? { custom_domain_environment: extra.environment } : {}),
      ...(extra.confirm_tls ? { confirm_tls: true } : {}),
    },
  });
}

// ── App to Device Links ─────────────────────────────────────────────────────

export async function listAppDeviceLinks(appId: string): Promise<{ success: boolean; links: any[] }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(appId)}/devices`, { method: 'GET', headers: getAuthHeaders() });
}

export async function linkDeviceToApp(appId: string, deviceId: string, scopes: string[] = []): Promise<{ success: boolean; link: any }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(appId)}/devices`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ device_id: deviceId, scopes }),
  });
}

export async function unlinkDeviceFromApp(appId: string, deviceId: string): Promise<{ success: boolean; unlinked: boolean }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(appId)}/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE', headers: getAuthHeaders() });
}

export async function listAppHandoffNotes(app_id: string, input?: { limit?: number; includeArchived?: boolean }): Promise<{ notes: any[] }> {
  const qs = new URLSearchParams();
  if (input?.limit != null) qs.set('limit', String(input.limit));
  if (input?.includeArchived) qs.set('include_archived', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/handoff-notes${suffix}`, { method: 'GET', headers: getAuthHeaders() });
}

export async function archiveMailboxItem(app_id: string, item_id: string, source: 'note' | 'post'): Promise<{ success: boolean }> {
  const segment = source === 'post' ? 'handoff-posts' : 'handoff-notes';
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/${segment}/${encodeURIComponent(item_id)}/archive`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
}

export async function unarchiveMailboxItem(app_id: string, item_id: string, source: 'note' | 'post'): Promise<{ success: boolean }> {
  const segment = source === 'post' ? 'handoff-posts' : 'handoff-notes';
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/${segment}/${encodeURIComponent(item_id)}/unarchive`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
}

export async function getMailboxUnreadSummary(
  appIds: string[],
  since: string | string[]
): Promise<{ summary: Array<{ app_id: string; count: number }> }> {
  const qs = new URLSearchParams();
  qs.set('app_ids', appIds.join(','));
  const sinceValue = Array.isArray(since) ? since.join(',') : since;
  if (sinceValue) qs.set('since', sinceValue);
  return apiRequest(`${APPS_BASE}/mailbox-unread?${qs.toString()}`, { method: 'GET', headers: getAuthHeaders() });
}

export async function deployAppBackend(
  app_id: string,
  body: Record<string, unknown> = {}
): Promise<{ ok?: boolean; workflow_run_id?: string; [key: string]: unknown }> {
  return apiRequest(`${APPS_BASE}/${encodeURIComponent(app_id)}/deploy-backend`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
}

export async function deployAppBuild(app_id: string, buildFile: File, deployTarget?: 'production' | 'sandbox'): Promise<any> {
  const headers = getAuthHeaders();
  headers.delete('Content-Type');
  const form = new FormData();
  form.append('build', buildFile);
  if (deployTarget) form.append('deploy_target', deployTarget);

  const url = `${APPS_BASE}/${encodeURIComponent(app_id)}/deploy`;
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    headers,
    body: form,
  });
  const text = await res.text().catch(() => '');
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || `${res.status} ${res.statusText}`;
    throw new Error(String(msg));
  }
  return data;
}

export async function listBuildSessions(
  app_id: string,
  tenant_id = 'dev',
  input?: { limit?: number; status?: string }
): Promise<{ ok?: boolean; sessions?: any[]; count?: number; error?: string }> {
  const qs = new URLSearchParams();
  qs.set('app_id', app_id);
  qs.set('tenant_id', tenant_id);
  if (input?.limit != null) qs.set('limit', String(input.limit));
  if (input?.status) qs.set('status', input.status);
  return apiRequest(`${CP_BASE}/build-sessions?${qs.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
}
